import { activationCodeSourceEnum, activationCodeStatusEnum } from '../server/db/schema/activation';

import { z } from 'zod';

// Common ID schema
export const IdSchema = z.object({
  id: z.string().uuid(),
});

export const CodeSchema = z.object({
  code: z.string().min(1),
});

// 自定义特性项类型定义
export const CustomFeatureItemSchema = z.object({
  id: z.string().optional(), // Added for client-side form state compatibility
  keyName: z.string().min(1, "Feature key is required.").regex(/^[a-z]+(?:[A-Z][a-z\d]*)*$/, "Key must be in camelCase format (e.g., myFeatureKey)"),
  value: z.union([z.string(), z.number(), z.boolean()]),
  valueType: z.enum(["text", "number", "boolean"]),
});

export type CustomFeatureItem = z.infer<typeof CustomFeatureItemSchema>;

// 自定义特性类型：表单侧主要使用数组形式
export const CustomFeaturesSchema = z.array(CustomFeatureItemSchema);

// 如果API仍然需要接收对象形式，转换将在提交时处理
// export const CustomFeaturesApiSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean()]));

// Product Schemas
export const CreateProductSchema = z.object({
  name: z.string().min(1, "Product name is required."),
  description: z.string().optional(),
  monthlyPrice: z.number().nonnegative().optional(),
  yearlyPrice: z.number().nonnegative().optional(),
  featureIds: z.array(z.string().uuid()).optional().default([]), // Array of feature IDs
  customFeatures: CustomFeaturesSchema.optional().default([]),
  monthlyPurchasable: z.boolean().optional().default(false),
  yearlyPurchasable: z.boolean().optional().default(false),
  maxBindingsPerCode: z.number().int().min(0).optional().default(1), // 0 for unlimited, default 1
  position: z.number().int().optional(), // For product sorting order
});
export type CreateProductInput = z.infer<typeof CreateProductSchema>;

export const UpdateProductSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).optional(),
  description: z.string().optional().nullable(), // Also allow null for description if it can be cleared
  monthlyPrice: z.number().nonnegative().optional().nullable(),
  yearlyPrice: z.number().nonnegative().optional().nullable(),
  featureIds: z.array(z.string().uuid()).optional(), // If provided, will replace existing features
  customFeatures: CustomFeaturesSchema.optional().nullable(), // Assuming custom features can also be cleared to null
  monthlyPurchasable: z.boolean().optional(),
  maxBindingsPerCode: z.number().int().min(0).optional(),
  yearlyPurchasable: z.boolean().optional(),
  position: z.number().int().optional(), // For product sorting order
});
export type UpdateProductInput = z.infer<typeof UpdateProductSchema>;

// Activation Code Schemas
export const GenerateActivationCodeSchema = z.object({
  productId: z.string().uuid("Invalid Product ID."),
  assignedEmail: z.string().email("Invalid email format.").optional(),
  expiresAt: z.coerce.date().min(new Date(), { message: "Expiry date must be in the future." }),
  quantity: z.number().int().min(1).max(100).default(1), // For generating multiple codes at once
  source: z.enum(activationCodeSourceEnum.enumValues).default("manual"), // Source of the activation code
});
export type GenerateActivationCodeInput = z.infer<typeof GenerateActivationCodeSchema>;

export const ListActivationCodesSchema = z.object({
  productId: z.string().uuid().optional(),
  assignedEmail: z.string().email().optional(),
  status: z.enum(activationCodeStatusEnum.enumValues).optional(),
  source: z.enum(activationCodeSourceEnum.enumValues).optional(), // Source filter
  page: z.number().int().min(1).optional().default(1),
  pageSize: z.number().int().min(1).max(100).optional().default(10),
  sortBy: z.string().optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
});
export type ListActivationCodesInput = z.infer<typeof ListActivationCodesSchema>;

export const UpdateActivationCodeSchema = z.object({
  code: z.string().min(1, "Code is required."),
  expiresAt: z.coerce.date().min(new Date(), { message: "Expiry date must be in the future." }).optional(),
  status: z.enum(activationCodeStatusEnum.enumValues).optional(),
  assignedEmail: z.string().email("Invalid email format.").optional(),
  source: z.enum(activationCodeSourceEnum.enumValues).optional(), // Allow updating source
});
export type UpdateActivationCodeInput = z.infer<typeof UpdateActivationCodeSchema>;

export const ResetActivationCodeSchema = z.object({
  code: z.string().min(1, "Code is required."),
});
export type ResetActivationCodeInput = z.infer<typeof ResetActivationCodeSchema>;

export const ValidateActivationCodeSchema = z.object({
  code: z.string().min(1, "Code is required."),
  // Potentially add productId or user context if needed for more specific validation
});
export type ValidateActivationCodeInput = z.infer<typeof ValidateActivationCodeSchema>;

// Activation Code Binding Schemas
export const BindingTypeEnum = z.enum(["domain", "ip"]);

export const AddActivationCodeBindingSchema = z.object({
  code: z.string().min(1, "Activation code is required."),
  bindingType: BindingTypeEnum,
  bindingValue: z.string().min(1, "Binding value is required."),
}).refine((data) => { // Basic validation for IP or domain
  if (data.bindingType === BindingTypeEnum.Enum.ip) {
    // Basic IPv4 regex (simplified)
    return /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(data.bindingValue);
  }
  // Basic domain regex (simplified)
  return /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/.test(data.bindingValue) || data.bindingValue === 'localhost';
}, "Invalid domain or IP address format for the selected type.");
export type AddActivationCodeBindingInput = z.infer<typeof AddActivationCodeBindingSchema>;

export const RemoveActivationCodeBindingSchema = z.object({
  code: z.string().min(1, "Activation code is required."),
  bindingId: z.string().uuid("Binding ID is required."),
});
export type RemoveActivationCodeBindingInput = z.infer<typeof RemoveActivationCodeBindingSchema>;

export const ListActivationCodeBindingsSchema = z.object({
  code: z.string().min(1, "Activation code is required."),
});
export type ListActivationCodeBindingsInput = z.infer<typeof ListActivationCodeBindingsSchema>;

// Update ValidateActivationCodeSchema to include binding info for validation
export const ValidateActivationCodeWithBindingSchema = ValidateActivationCodeSchema.extend({
  bindingType: BindingTypeEnum.optional(),
  bindingValue: z.string().min(1).optional(),
}).refine(data => (data.bindingType && data.bindingValue) || (!data.bindingType && !data.bindingValue), {
  message: "Both bindingType and bindingValue must be provided together, or neither.",
  path: ["bindingValue"], // Path to the fields causing the error, can also be ["bindingType", "bindingValue"]
});
export type ValidateActivationCodeWithBindingInput = z.infer<typeof ValidateActivationCodeWithBindingSchema>;

// Schema for updating the order of a single product item in a list
export const UpdateProductOrderItemSchema = z.object({
  id: z.string().uuid("Product ID must be a valid UUID."),
  position: z.number().int("Position must be an integer."),
});
export type UpdateProductOrderItemInput = z.infer<typeof UpdateProductOrderItemSchema>;

// Schema for updating the order of multiple products
export const UpdateProductOrderSchema = z.object({
  orderedProducts: z.array(UpdateProductOrderItemSchema).min(1, "At least one product order must be provided."),
});
export type UpdateProductOrderInput = z.infer<typeof UpdateProductOrderSchema>;

// Schema for the output of the queryDomainLicense procedure
export const QueryDomainLicenseOutputSchema = z.object({
  isAuthorized: z.boolean(),
  version: z.string().optional(),
  queriedDomain: z.string(), // Matches existing frontend assumption
  message: z.string().optional(),
  expiresAt: z.string().datetime({ offset: true }).optional(), // ISO date string, optional
});
export type QueryDomainLicenseOutput = z.infer<typeof QueryDomainLicenseOutputSchema>;